import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useProfiles } from "@/hooks/useProfiles";
import { Project } from "@/hooks/useProjects";

export type UserRole = 'ADMIN' | 'SUPERVISOR' | 'PADRAO' | 'VISUALIZACAO';

export const usePermissions = () => {
  const { user } = useAuth();
  const { data: profiles = [], isLoading } = useProfiles();
  const [isProfileReady, setIsProfileReady] = React.useState(false);

  const getCurrentUserProfile = () => {
    if (!user) return null;
    return profiles.find(p => p.id === user.id);
  };

  // Garantir que o perfil está carregado (simplificado sem timeout)
  React.useEffect(() => {
    // Se não há usuário ou ainda está carregando, não está pronto
    if (!user || isLoading) {
      setIsProfileReady(false);
      return;
    }
    
    // Se profiles foram carregados (mesmo que vazio ou sem encontrar o perfil específico),
    // considerar pronto para não bloquear a UI
    setIsProfileReady(true);
  }, [user, isLoading]);

  const getUserRole = (): UserRole => {
    const profile = getCurrentUserProfile();
    return (profile?.role as UserRole) || 'PADRAO';
  };

  const canEditProject = (project: Project) => {
    if (!user) return false;
    
    const currentProfile = getCurrentUserProfile();
    if (!currentProfile) return false;

    // Admin e Supervisor podem editar qualquer projeto
    if (['ADMIN', 'SUPERVISOR'].includes(currentProfile.role)) {
      return true;
    }

    // Coordenador SGTI pode editar
    if (project.sgti_coordinator_id === user.id) {
      return true;
    }

    // Usuários na lista de editores podem editar
    if (project.editor_ids && project.editor_ids.includes(user.id)) {
      return true;
    }

    return false;
  };

  const canViewProject = (project: Project) => {
    if (!user) return false;
    
    const currentProfile = getCurrentUserProfile();
    if (!currentProfile) return false;

    // Admin e Supervisor podem ver qualquer projeto
    if (['ADMIN', 'SUPERVISOR'].includes(currentProfile.role)) {
      return true;
    }

    // Usuários só veem projetos onde estão nomeados
    return (
      project.sgti_coordinator_id === user.id ||
      project.requisitante_id === user.id ||
      project.tecnico_id === user.id ||
      project.administrativo_id === user.id ||
      (project.editor_ids && project.editor_ids.includes(user.id)) ||
      (project.additional_members && project.additional_members.some((m: any) => m.memberId === user.id))
    );
  };

  const isAdmin = () => {
    const currentProfile = getCurrentUserProfile();
    return currentProfile?.role === 'ADMIN';
  };

  const isSupervisor = () => {
    const currentProfile = getCurrentUserProfile();
    return currentProfile?.role === 'SUPERVISOR';
  };

  const isViewOnly = () => {
    const currentProfile = getCurrentUserProfile();
    return currentProfile?.role === 'VISUALIZACAO';
  };

  const isPadrao = () => {
    const currentProfile = getCurrentUserProfile();
    return currentProfile?.role === 'PADRAO';
  };

  const canManageUsers = () => {
    const currentProfile = getCurrentUserProfile();
    return ['ADMIN', 'SUPERVISOR'].includes(currentProfile?.role || '');
  };

  // Capacidades granulares (além do papel). ADMIN/SUPERVISOR sempre podem.
  const canCreateProjects = () => {
    const p = getCurrentUserProfile();
    if (!p) return false;
    if (['ADMIN', 'SUPERVISOR'].includes(p.role)) return true;
    return (p as any).can_create_projects === true;
  };

  const canCreateContracts = () => {
    const p = getCurrentUserProfile();
    if (!p) return false;
    if (['ADMIN', 'SUPERVISOR'].includes(p.role)) return true;
    return (p as any).can_create_contracts === true;
  };

  const canCreateActionPlans = () => {
    const p = getCurrentUserProfile();
    if (!p) return false;
    if (['ADMIN', 'SUPERVISOR'].includes(p.role)) return true;
    return (p as any).can_create_action_plans === true;
  };

  const canCreateStrategicItems = () => {
    const p = getCurrentUserProfile();
    if (!p) return false;
    if (['ADMIN', 'SUPERVISOR'].includes(p.role)) return true;
    return (p as any).can_create_strategic_items === true;
  };

  // ---------------------------------------------------------------------------
  // Visibilidade granular por módulo — SÓ afeta PADRAO e VISUALIZACAO
  // ADMIN/SUPERVISOR sempre veem tudo. Flags têm default TRUE no banco, portanto
  // nenhum usuário existente perde acesso ao ativarmos esta camada.
  // ---------------------------------------------------------------------------
  type ViewModuleKey =
    | 'dashboard'
    | 'projects'
    | 'contracts'
    | 'portfolios'
    | 'strategic'
    | 'calendar'
    | 'tasks';

  type ReportsTabKey =
    | 'overview_projects'
    | 'overview_contracts'
    | 'financial'
    | 'users'
    | 'areas';

  const canViewModule = (moduleKey: ViewModuleKey): boolean => {
    const p = getCurrentUserProfile();
    if (!p) return false;
    const role = p.role as UserRole;
    if (role === 'ADMIN' || role === 'SUPERVISOR') return true;
    // Apenas PADRAO e VISUALIZACAO são afetados
    const flag = (p as any)[`can_view_${moduleKey}`];
    return flag !== false; // undefined ou true => permite
  };

  const canViewReportsTab = (tab: ReportsTabKey): boolean => {
    const p = getCurrentUserProfile();
    if (!p) return false;
    const role = p.role as UserRole;
    if (role === 'ADMIN' || role === 'SUPERVISOR') return true;
    const flag = (p as any)[`can_view_reports_${tab}`];
    return flag !== false;
  };

  const hasAnyReportsTab = (): boolean =>
    canViewReportsTab('overview_projects') ||
    canViewReportsTab('overview_contracts') ||
    canViewReportsTab('financial') ||
    canViewReportsTab('users') ||
    canViewReportsTab('areas');

  // Novos métodos para controle de acesso a módulos
  const canAccessDashboard = () => {
    return ['ADMIN', 'SUPERVISOR'].includes(getUserRole());
  };

  const canAccessTasks = () => {
    return ['ADMIN', 'SUPERVISOR', 'PADRAO'].includes(getUserRole()) && canViewModule('tasks');
  };

  const canAccessReports = () => {
    if (['ADMIN', 'SUPERVISOR'].includes(getUserRole())) return true;
    return hasAnyReportsTab();
  };

  const canAccessPortfolios = () => {
    if (['ADMIN', 'SUPERVISOR'].includes(getUserRole())) return true;
    // Permite Padrão/Visualização apenas com flag explícita (default true, mas só entra aqui se algum dia liberarmos rota)
    return canViewModule('portfolios');
  };

  const canAccessCalendar = () => {
    return ['ADMIN', 'SUPERVISOR', 'PADRAO'].includes(getUserRole()) && canViewModule('calendar');
  };

  const canAccessAdmin = () => {
    return ['ADMIN', 'SUPERVISOR'].includes(getUserRole());
  };

  const canAccessTrash = () => {
    return ['ADMIN', 'SUPERVISOR'].includes(getUserRole());
  };

  const canAccessStrategicModule = (hasStrategicParticipation: boolean = false) => {
    const role = getUserRole();
    if (['ADMIN', 'SUPERVISOR'].includes(role)) {
      return true;
    }
    return hasStrategicParticipation && canViewModule('strategic');
  };

  return {
    user,
    getCurrentUserProfile,
    getUserRole,
    canEditProject,
    canViewProject,
    isAdmin,
    isSupervisor,
    isViewOnly,
    isPadrao,
    canManageUsers,
    canCreateProjects,
    canCreateContracts,
    canCreateActionPlans,
    canCreateStrategicItems,
    canAccessDashboard,
    canAccessTasks,
    canAccessReports,
    canAccessPortfolios,
    canAccessCalendar,
    canAccessAdmin,
    canAccessTrash,
    canAccessStrategicModule,
    canViewModule,
    canViewReportsTab,
    isLoadingPermissions: isLoading || !isProfileReady
  };
};
